import BigNumber from 'bignumber.js';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { use } from 'react';

import { CurrencyV2Instance } from '@flipster/apis/v1/currency/instance';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { Badge } from '@flipster/react/design-system/badge';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { Price } from '@flipster/ui';
import { AssetPrecision, UsdtPrecision } from '@flipster/utils/constants/currency';

import { CurrencyDisplay } from '../../../../misc/display';
import { useSnapshotSpec } from '../../../snapshot/use-snapshot-spec';
import { WalletModalTypeContext } from '../core/state';

interface Props {
  item: CurrencyV2Instance;
  earnAppliedAprByCurrency?: Record<string, BigNumber>;
}

export const AssetItem = ({ item, earnAppliedAprByCurrency }: Props) => {
  const type = use(WalletModalTypeContext);
  const { walletsAtom } = useBunja(walletsBunja);
  const wallet = useAtomValue(walletsAtom);
  const { currenciesKeyByCurrency } = useSnapshotSpec();

  const currencyWallet = wallet?.[item.currency];
  const currencyInstrument = currenciesKeyByCurrency?.[item.currency];

  const earnAppliedApr = type === 'deposit' && earnAppliedAprByCurrency?.[item.currency];
  const zeroFee = type === 'withdraw' && Boolean(currencyInstrument?.withdrawalBadgeString);

  const highlightText = earnAppliedApr ? `${earnAppliedApr.toString()}% APR` : '';

  return (
    <div
      className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        color: 'content.default.level-5',
        textAlign: 'initial',
      })}
    >
      <CurrencyDisplay
        currency={item.currency}
        icon={<CurrencyDisplay.Icon size={24} space={8} />}
        mainLabel={
          <div className={hstack({ gap: 4 })}>
            <CurrencyDisplay.Label
              value='fullName'
              css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
            />
            {zeroFee && <Badge color='neutral'>{currencyInstrument?.withdrawalBadgeString}</Badge>}
            {earnAppliedApr && <Badge color='positive'>{highlightText}</Badge>}
          </div>
        }
        subLabel={<div className={css({ textStyle: 'Text/XS/Regular' })}>{item.currency}</div>}
      />
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'right',
          whiteSpace: 'nowrap',
        })}
      >
        <Price
          fontType='num2'
          price={
            type === 'withdraw'
              ? currencyWallet?.walletBalanceWithdrawable
              : currencyWallet?.walletBalanceExclWithdrawalPending
          }
          precision={AssetPrecision}
          currency={item.currency}
          className={css({
            color: 'content.default.level-5',
          })}
        />
        <Price
          fontType='num3'
          price={
            type === 'withdraw'
              ? currencyWallet?.walletBalanceWithdrawableInUsdt
              : currencyWallet?.walletBalanceExclWithdrawalPendingInUsdt
          }
          precision={UsdtPrecision}
          className={css({
            color: 'content.default.level-2',
          })}
        />
      </div>
    </div>
  );
};
